import type { ReactNode } from 'react';
import { ArrowRight } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Icon } from '../Icon';

export type PageHeaderProps = {
  title: ReactNode;
  /** Only when it adds context (the hub or the category). */
  eyebrow?: ReactNode;
  subtitle?: ReactNode;
  /** Buttons beside the title on wide screens, below it on phones. */
  actions?: ReactNode;
  /** Breadcrumbs above everything. */
  breadcrumbs?: ReactNode;
  /** h1 by default: one per page. */
  as?: 'h1' | 'h2';
  className?: string;
};

/** The top of a page: eyebrow, the one H1 on the display scale by breakpoint, a subtitle and actions. */
export function PageHeader({
  title,
  eyebrow,
  subtitle,
  actions,
  breadcrumbs,
  as: Heading = 'h1',
  className,
}: PageHeaderProps) {
  return (
    <header className={cx('flex flex-col gap-4', className)}>
      {breadcrumbs}
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div className="flex min-w-0 flex-col gap-2">
          {eyebrow ? <span className="type-eyebrow text-jc-ink-secondary">{eyebrow}</span> : null}
          <Heading className="type-display text-jc-ink">{title}</Heading>
          {subtitle ? <p className="max-w-2xl text-base text-jc-ink-secondary">{subtitle}</p> : null}
        </div>
        {actions ? <div className="flex shrink-0 flex-wrap gap-2">{actions}</div> : null}
      </div>
    </header>
  );
}

export type SectionHeaderProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  /** A "See all" link, in the tint colour. */
  action?: { href: string; label: ReactNode };
  as?: 'h2' | 'h3';
  /** Heading id, for aria-labelledby on the section. */
  id?: string;
  className?: string;
};

/** The header of a section on a page: a title with an optional See all link. */
export function SectionHeader({ title, subtitle, action, as: Heading = 'h2', id, className }: SectionHeaderProps) {
  return (
    <div className={cx('flex items-end justify-between gap-4', className)}>
      <div className="flex min-w-0 flex-col gap-1">
        <Heading id={id} className="type-title text-jc-ink">
          {title}
        </Heading>
        {subtitle ? <p className="text-sm text-jc-ink-secondary">{subtitle}</p> : null}
      </div>
      {action ? (
        <a
          href={action.href}
          className="inline-flex shrink-0 items-center gap-1 rounded-jc-info text-sm font-semibold text-jc-tint hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus"
        >
          {action.label}
          <Icon icon={ArrowRight} size="sm" />
        </a>
      ) : null}
    </div>
  );
}
